import { useState } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import useApi from '../../../hooks/useApi'
import useTranslation from '../../../hooks/useTranslation'
import useProfile from '../../../hooks/useProfile'
import { ossReturn, ossThreshold } from '../../../../../common/utils/tax/ossReturn.js'

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
`

const Header = styled.div`
    padding: 1rem 1.5rem 0.75rem;
    border-bottom: 1px solid ${p => p.theme.form.border};
    flex-shrink: 0;
    display: flex;
    align-items: baseline;
    gap: 1.5rem;
    flex-wrap: wrap;
`

const Title = styled.h2`
    font-size: 1.1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 0;
`

const Period = styled.label`
    font-size: 0.85rem;
    color: ${p => p.theme.font.label};
    display: flex;
    align-items: center;
    gap: 0.4rem;
`

const DateInput = styled.input`
    font: inherit;
    padding: 0.2rem 0.4rem;
    color: ${p => p.theme.font.color};
    background: ${p => p.theme.form.background};
    border: 1px solid ${p => p.theme.form.border};
    border-radius: 4px;
`

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.5rem;
`

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
`

const Th = styled.th`
    text-align: left;
    padding: 0.5rem 0.75rem;
    color: ${p => p.theme.font.label};
    font-weight: 600;
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const Td = styled.td`
    padding: 0.4rem 0.75rem;
    color: ${p => p.theme.font.color};
    border-bottom: 1px solid ${p => p.theme.form.border};
`

const Num = styled(Td)`
    text-align: right;
    font-variant-numeric: tabular-nums;
`

const JurisdictionRow = styled.tr`
    font-weight: 700;
    border-top: 2px solid ${p => p.theme.form.border};
`

const WarnRow = styled.tr`
    background: ${p => p.theme.form.background};
`

const Note = styled.p`
    font-size: 0.8rem;
    color: ${p => p.theme.font.label};
    margin: 1rem 0 0;
    max-width: 60ch;
`

const money = (v) => (v === null || v === undefined ? '' : Number(v).toFixed(2))

/*
 * The VAT return: what was taxed in a period, grouped the way the authority
 * asks for it.
 *
 * The figures come from ledgerEntry/agr/byTaxBox, which unnests each posted
 * row's taxReturnBoxes — one ledger row commonly reports to several boxes at
 * once, so the same amount legitimately appears under more than one. That is
 * why the columns are NOT totalled down the page: summing boxes would add the
 * same VAT two or three times. Totals are per jurisdiction, and the net
 * position comes from the VAT accounts, not from adding this up.
 *
 * `recoverable` differs from `vat` wherever input VAT is only partly
 * deductible; the difference is the part that stayed with the cost.
 */
const VatReturnPage = () => {
    const { t } = useTranslation()
    const { getMe } = useApi()

    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')

    const params = new URLSearchParams()
    if (fromDate) params.set('fromDate', fromDate)
    if (toDate) params.set('toDate', toDate)
    const qs = params.toString()

    const { data, isLoading } = useQuery({
        queryKey: ['ledgerEntry', 'byTaxBox', fromDate, toDate],
        queryFn: () => getMe(`ledgerEntry/agr/byTaxBox${qs ? `?${qs}` : ''}`),
    })

    const rows = Array.isArray(data?.data) ? data.data : []

    // Group by jurisdiction — a return is filed per jurisdiction, never across.
    const byJurisdiction = rows.reduce((acc, r) => {
        const key = r.taxjurisdiction || '—'
        ;(acc[key] ??= []).push(r)
        return acc
    }, {})

    // VAT that reaches no box is VAT the return does not show. The aggregation
    // surfaces it with a null box rather than dropping it, and this is where
    // that pays off. Two causes: a code genuinely mapped to nothing, or a
    // country whose box mapping is not modelled yet (ES, PT and IT carry their
    // official codes but no boxes). A zero-VAT row with no box is neither — an
    // import invoice without VAT legitimately reports nowhere — so it does not
    // raise the warning.
    const orphanCount = rows.filter(r => !r.box && Number(r.vat) !== 0).length

    return (
        <PageWrap>
            <Header>
                <Title>{t('accounting.vatReturn.title')}</Title>
                <Period>
                    {t('accounting.vatReturn.from')}
                    <DateInput type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                </Period>
                <Period>
                    {t('accounting.vatReturn.to')}
                    <DateInput type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                </Period>
            </Header>
            <Body>
                {isLoading ? (
                    <div>…</div>
                ) : rows.length === 0 ? (
                    <div>{t('accounting.vatReturn.empty')}</div>
                ) : (
                    <>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>{t('accounting.vatReturn.table.box')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.base')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.vat')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.recoverable')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.cost')}</Th>
                                    <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.entries')}</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(byJurisdiction).map(([jurisdiction, boxes]) => (
                                    <Fragmentish key={jurisdiction}>
                                        <JurisdictionRow>
                                            <Td colSpan={6}>{jurisdiction}</Td>
                                        </JurisdictionRow>
                                        {boxes
                                            .slice()
                                            .sort((a, b) => (a.box || '').localeCompare(b.box || ''))
                                            .map(row => {
                                                const Row = row.box ? 'tr' : WarnRow
                                                return (
                                                    <Row key={`${jurisdiction}-${row.box || 'none'}`}>
                                                        <Td>{row.box || t('accounting.vatReturn.table.noBox')}</Td>
                                                        <Num>{money(row.base)}</Num>
                                                        <Num>{money(row.vat)}</Num>
                                                        <Num>{money(row.recoverable)}</Num>
                                                        <Num>{money(row.cost)}</Num>
                                                        <Num>{row.entries}</Num>
                                                    </Row>
                                                )
                                            })}
                                    </Fragmentish>
                                ))}
                            </tbody>
                        </Table>
                        {orphanCount > 0 && (
                            <Note>{t('accounting.vatReturn.noBox.warning')}</Note>
                        )}
                        <Note>{t('accounting.vatReturn.note')}</Note>
                    </>
                )}
                <ProRataAdjustment fromDate={fromDate} toDate={toDate} />
                <OssReturn fromDate={fromDate} toDate={toDate} />
                <OssThreshold toDate={toDate} />
            </Body>
        </PageWrap>
    )
}

// A <tbody> may not nest, and a fragment cannot take a key in the shorthand
// form — this keeps each jurisdiction's rows together without adding markup.
const Fragmentish = ({ children }) => <>{children}</>

const SectionTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    color: ${p => p.theme.font.color};
    margin: 2rem 0 0.5rem;
`

const PctInput = styled(DateInput)`
    width: 6rem;
`

/*
 * The year-end pro rata correction, calculated.
 *
 * During the year, pro rata purchases are deducted at a provisional share. Once
 * the year's actual turnover split is known, the deduction is corrected to the
 * final share. This computes that correction for the period above: the VAT on
 * rows posted under a `partial` deduction method, what was deducted on them,
 * what the final percentage allows, and the difference.
 *
 * It calculates and does not post. Which accounts the correction goes to —
 * back to each original expense line, or one adjustment account — is the
 * company's choice, and a manual journal expresses it.
 */
const ProRataAdjustment = ({ fromDate, toDate }) => {
    const { t } = useTranslation()
    const { getMe } = useApi()
    const [finalPct, setFinalPct] = useState('')

    const params = new URLSearchParams({ taxDeductionMethod: 'partial' })
    if (fromDate) params.set('fromDate', fromDate)
    if (toDate) params.set('toDate', toDate)

    const { data } = useQuery({
        queryKey: ['ledgerEntry', 'byDeductionMethod', 'partial', fromDate, toDate],
        queryFn: () => getMe(`ledgerEntry/agr/byDeductionMethod?${params.toString()}`),
    })
    const rows = Array.isArray(data?.data) ? data.data : []
    if (rows.length === 0) return null

    const pct = finalPct === '' ? null : Number(finalPct)
    const valid = pct !== null && Number.isFinite(pct) && pct >= 0 && pct <= 100

    return (
        <>
            <SectionTitle>{t('accounting.vatReturn.proRata.title')}</SectionTitle>
            <Period>
                {t('accounting.vatReturn.proRata.finalPct')}
                <PctInput type="number" min="0" max="100" step="0.01" value={finalPct}
                    onChange={e => setFinalPct(e.target.value)} />
            </Period>
            <Table>
                <thead>
                    <tr>
                        <Th>{t('accounting.vatReturn.proRata.jurisdiction')}</Th>
                        <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.proRata.vat')}</Th>
                        <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.proRata.deducted')}</Th>
                        <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.proRata.deductible')}</Th>
                        <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.proRata.adjustment')}</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const vat = Number(row.vat) || 0
                        const deducted = Number(row.recoverable) || 0
                        const deductible = valid ? Math.round(vat * pct) / 100 : null
                        return (
                            <tr key={row.taxjurisdiction || '—'}>
                                <Td>{row.taxjurisdiction || '—'}</Td>
                                <Num>{money(vat)}</Num>
                                <Num>{money(deducted)}</Num>
                                <Num>{money(deductible)}</Num>
                                <Num>{deductible === null ? '' : money(deductible - deducted)}</Num>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
            <Note>{t('accounting.vatReturn.proRata.note')}</Note>
        </>
    )
}

// ─── One Stop Shop ───────────────────────────────────────────────────────────

const isoDay = (d) => d.toISOString().slice(0, 10)

/*
 * The ECB rate the OSS figures convert at: <base currency> per EUR on `day`,
 * or the next day the ECB publishes (GET fxRate/ecb). None for a EUR company.
 */
const useEcbRate = (day, baseCurrency) => {
    const { getMe } = useApi()
    const needed = !!day && !!baseCurrency && baseCurrency !== 'EUR'
    const { data } = useQuery({
        queryKey: ['fxRate', 'ecb', day, baseCurrency],
        queryFn: () => getMe(`fxRate/ecb/${day}/${baseCurrency}`),
        enabled: needed,
        staleTime: Infinity
    })
    return needed && data?.rate > 0 ? data : null
}

const Total = styled.tr`
    font-weight: 700;
`

/*
 * The quarterly OSS return: sales to private customers in other EU countries
 * under the One Stop Shop, per country and rate, in EUR (ossReturn in
 * common/utils/tax/ossReturn.js). Converted at the ECB rate of the period's
 * last day — the "To" date — as the OSS rules require; choosing the quarter's
 * first and last day gives the figures to file.
 */
const OssReturn = ({ fromDate, toDate }) => {
    const { t, m } = useTranslation()
    const { getMe } = useApi()
    const { pState } = useProfile()
    const baseCurrency = pState.currentOrgCurrency

    const params = new URLSearchParams({ taxScheme: 'oss' })
    if (fromDate) params.set('fromDate', fromDate)
    if (toDate) params.set('toDate', toDate)
    const { data } = useQuery({
        queryKey: ['ledgerEntry', 'byOssCode', fromDate, toDate],
        queryFn: () => getMe(`ledgerEntry/agr/byOssCode?${params.toString()}`)
    })
    const ecb = useEcbRate(toDate, baseCurrency)

    const rows = Array.isArray(data?.data) ? data.data : []
    const report = ossReturn(rows, { baseCurrency, eurRate: ecb?.rate })
    if (rows.length === 0) return null

    const pct = (rate) => `${Math.round(rate * 1000) / 10} %`
    return (
        <>
            <SectionTitle>{t('accounting.vatReturn.oss.title')}</SectionTitle>
            {report.lines.length > 0 && (
                <Table>
                    <thead>
                        <tr>
                            <Th>{t('accounting.vatReturn.oss.country')}</Th>
                            <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.oss.rate')}</Th>
                            <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.oss.taxable')}</Th>
                            <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.oss.vat')}</Th>
                            <Th style={{ textAlign: 'right' }}>{t('accounting.vatReturn.table.entries')}</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.lines.map(l => (
                            <tr key={`${l.country}-${l.rate}`}>
                                <Td>{l.country}</Td>
                                <Num>{pct(l.rate)}</Num>
                                <Num>{money(l.taxable)}</Num>
                                <Num>{money(l.vat)}</Num>
                                <Num>{l.entries}</Num>
                            </tr>
                        ))}
                        <Total>
                            <Td colSpan={2}>{t('accounting.vatReturn.oss.total')}</Td>
                            <Num>{money(report.total.taxable)}</Num>
                            <Num>{money(report.total.vat)}</Num>
                            <Td />
                        </Total>
                    </tbody>
                </Table>
            )}
            {report.needsRate && <Note>{t('accounting.vatReturn.oss.needsRate')}</Note>}
            {ecb && <Note>{m('accounting.vatReturn.oss.ecb', [ecb.rate, baseCurrency, ecb.date])}</Note>}
            <Note>{t('accounting.vatReturn.oss.note')}</Note>
        </>
    )
}

/*
 * Sales to private customers in other EU countries this year, against the
 * EUR 10,000 above which OSS — or registering in each country — is required
 * (ossThreshold). The year is the "To" date's, or this one.
 */
const OssThreshold = ({ toDate }) => {
    const { t, m } = useTranslation()
    const { getMe } = useApi()
    const { pState } = useProfile()
    const baseCurrency = pState.currentOrgCurrency
    const orgCountry = pState.currentOrgCountry

    const year = (toDate || isoDay(new Date())).slice(0, 4)
    const range = new URLSearchParams({ state: 'final', fromDate: `${year}-01-01`, toDate: `${year}-12-31T23:59:59.999Z` }).toString()
    const { data: invoices } = useQuery({
        queryKey: ['invoice', 'byCustomerCountry', year],
        queryFn: () => getMe(`invoice/agr/byCustomerCountry?${range}`)
    })
    const { data: creditNotes } = useQuery({
        queryKey: ['creditNote', 'byCustomerCountry', year],
        queryFn: () => getMe(`creditNote/agr/byCustomerCountry?${range}`)
    })
    // The year's last day, or three days ago for the running year: the ECB
    // publishes on working days, and its next publication must already exist.
    const recent = new Date(Date.now() - 3 * 86400000)
    const rateDay = [`${year}-12-31`, isoDay(recent)].sort()[0]
    const ecb = useEcbRate(rateDay, baseCurrency)

    const list = (d) => (Array.isArray(d?.data) ? d.data : [])
    const result = ossThreshold(list(invoices), list(creditNotes), { orgCountry, baseCurrency, eurRate: ecb?.rate })
    if (result.base === 0) return null

    return (
        <>
            <SectionTitle>{m('accounting.vatReturn.oss.threshold.title', [year])}</SectionTitle>
            <Note>
                {result.eur === null
                    ? m('accounting.vatReturn.oss.threshold.base', [money(result.base), baseCurrency])
                    : m('accounting.vatReturn.oss.threshold.eur', [money(result.eur), money(result.threshold)])}
            </Note>
            {result.exceeded && <Note><strong>{t('accounting.vatReturn.oss.threshold.exceeded')}</strong></Note>}
            <Note>{t('accounting.vatReturn.oss.threshold.note')}</Note>
        </>
    )
}

export default VatReturnPage
